import { uuid } from "vue3-uuid";

const isPositiveLimit = (value: unknown): value is number =>
  value != null && Number(value) > 0;

export const extractBetSettings = (source: Record<string, any> = {}) => ({
  bet_amount_values: source.bet_amount_values ?? [],
  bet_default: source.bet_default ?? 1,
  bet_max: source.bet_max ?? null,
  bet_min: source.bet_min ?? 1,
  payout_max: source.payout_max ?? null,
  allow_decimals: source.allow_decimals ?? true,
});
export function resolveNextDrawMultiplier(
  nextDraws?: number | null
): number {
  if (nextDraws == null || nextDraws <= 0) return 1;
  return nextDraws;
}

export function calculateBetReturnValue(
  wager: number,
  odds: number,
  nextDraws?: number | null
) {
  const draws = resolveNextDrawMultiplier(nextDraws);
  return wager * odds * draws;
}

/** Per-draw wager limits; payout_max applies to a single draw only. */
export function clampWagerByBetSettings(
  wager: number,
  odds: number,
  settings: ReturnType<typeof extractBetSettings> = extractBetSettings()
) {
  const allowDecimals = settings.allow_decimals !== false;
  let amount = Number(wager) || 0;

  if (!allowDecimals) {
    // Whole-unit currency: the lower bound rounds UP and the upper bounds round
    // DOWN, so the result can never be floored below bet_min. No bet_min means no
    // lower bound (0), so an empty or sub-unit request stays 0 and is blocked,
    // rather than promoted to a 1-unit bet the player never asked for.
    const lower = isPositiveLimit(settings.bet_min) ? Math.ceil(settings.bet_min) : 0;
    let upper = Infinity;
    if (isPositiveLimit(settings.bet_max)) {
      upper = Math.min(upper, Math.floor(settings.bet_max));
    }
    if (isPositiveLimit(settings.payout_max) && odds > 0) {
      upper = Math.min(upper, Math.floor(settings.payout_max / odds));
    }
    amount = Math.floor(amount);
    if (amount < lower) amount = lower;
    if (amount > upper) amount = upper;
    return amount;
  }

  if (isPositiveLimit(settings.bet_min) && amount < settings.bet_min) {
    amount = settings.bet_min;
  }
  if (isPositiveLimit(settings.bet_max) && amount > settings.bet_max) {
    amount = settings.bet_max;
  }
  if (isPositiveLimit(settings.payout_max) && odds > 0) {
    const maxWagerByPayout =
      Math.floor((settings.payout_max / odds) * 100) / 100;
    if (amount > maxWagerByPayout) {
      amount = maxWagerByPayout;
    }
  }
  return amount;
}

/**
 * True when an (already clamped) stake can actually be submitted. When
 * payout_max / odds falls below bet_min (or to 0) the selection is unbettable and
 * the caller should say so instead of submitting a stake the API will reject.
 */
export function isStakeWithinLimits(
  wager: number,
  settings: ReturnType<typeof extractBetSettings> = extractBetSettings()
) {
  if (!wager || wager <= 0) return false;
  if (isPositiveLimit(settings.bet_min) && wager < settings.bet_min) return false;
  if (isPositiveLimit(settings.bet_max) && wager > settings.bet_max) return false;
  return true;
}

/**
 * Bet-limit settings for submitting a stake from an outcome: outcome-level limits win,
 * account-level limits fill any gap. Without the fallback, an outcome that arrives
 * without its limit fields would make clampWagerByBetSettings silently no-op.
 */
export function resolveOutcomeBetSettings(
  outcome: Record<string, unknown> | null | undefined,
  accountSetting: Record<string, unknown> = {}
) {
  return {
    bet_min: outcome?.bet_min ?? accountSetting.bet_min,
    bet_max: outcome?.bet_max ?? accountSetting.bet_max,
    payout_max: outcome?.payout_max ?? accountSetting.payout_max,
    bet_default: outcome?.bet_default ?? accountSetting.bet_default,
    bet_amount_values: outcome?.bet_amount_values ?? accountSetting.bet_amount_values,
    allow_decimals: outcome?.allow_decimals ?? accountSetting.allow_decimals,
  };
}

/**
 * Clamp an outcome's stake and report whether it can actually be submitted.
 * `valid === false` means the selection is unbettable at the current limits
 * (payout_max / odds below bet_min, or zero) — the caller should say so clearly
 * rather than submit a stake the API will reject.
 */
export function resolveStake(
  rawWager: unknown,
  outcome: Record<string, unknown> | null | undefined,
  accountSetting: Record<string, unknown> = {}
) {
  const settings = resolveOutcomeBetSettings(outcome, accountSetting);
  const wager = clampWagerByBetSettings(
    Number(rawWager) || 0,
    Number(outcome?.odds) || 0,
    settings
  );
  return { wager, settings, valid: isStakeWithinLimits(wager, settings) };
}

/**
 * True when at least one legal stake exists for this outcome. The render filter
 * and the submit guard both call this, so they can never disagree.
 *
 * Fails open: until the limits needed to decide are loaded, every outcome is
 * treated as bettable. `accountSetting` starts as `{}` and is filled from the
 * account payload, so filtering before that lands would render the coupon page
 * empty on first paint.
 */
export function isOutcomeBettable(
  outcome: Record<string, unknown> | null | undefined,
  accountSetting: Record<string, unknown> = {}
) {
  const settings = resolveOutcomeBetSettings(outcome, accountSetting);
  const odds = Number(outcome?.odds) || 0;
  const decidable =
    settings.bet_min != null &&
    (settings.bet_max != null || (settings.payout_max != null && odds > 0));
  if (!decidable) return true;
  const probe = Number(settings.bet_default) > 0 ? Number(settings.bet_default) : 1;
  return isStakeWithinLimits(clampWagerByBetSettings(probe, odds, settings), settings);
}

export function isPayoutAtMax(
  wager: number,
  odds: number,
  settings: ReturnType<typeof extractBetSettings> = extractBetSettings()
) {
  if (!isPositiveLimit(settings.payout_max) || !odds) return false;
  return wager * odds >= settings.payout_max;}

function filterAndSortNumbers(numbers: any[], targetValues: number[]) {
  return [...numbers]
    .filter(
      (n) => targetValues.length > 0 && targetValues.includes(n.value)
    )
    .sort(
      (a, b) =>
        targetValues.indexOf(a.value) - targetValues.indexOf(b.value)
    );
}

export function applyBetWager(
  bet: {
    wager: number;
    odds: number;
    next_draws?: number | null;
    returnValue: number;
    betSettings?: ReturnType<typeof extractBetSettings>;
  },
  wager: number
) {
  const drawCount = resolveNextDrawMultiplier(bet.next_draws);
  const settings = bet.betSettings || extractBetSettings();
  bet.wager = clampWagerByBetSettings(wager, bet.odds, settings);
  bet.returnValue = calculateBetReturnValue(bet.wager, bet.odds, drawCount);
}

export function isBetPayoutCapped(
  bet: {
    wager: number;
    odds: number;
    betSettings?: ReturnType<typeof extractBetSettings>;
  }
) {
  return isPayoutAtMax(
    bet.wager,
    bet.odds,
    bet.betSettings || extractBetSettings()
  );
}

export function convertApiResultToBets(
  apiResult: any[],
  gameMarket: any[],
  betSettings: ReturnType<typeof extractBetSettings>,
  options: { isCombination?: boolean; defaultWager?: number | null } = {}
) {
  const betInfo = apiResult[0];
  const balls =
    gameMarket.find((g) => g.slug === betInfo.game_slug)?.balls ?? [];
  const isCombination = options.isCombination ?? false;
  const settings = { ...betSettings };

  return betInfo.bets.map((item: any) => {
    const odds = +item.odds;
    const rawWager =
      options.defaultWager != null ? options.defaultWager : item.wager;
    const wager = clampWagerByBetSettings(rawWager, odds, settings);

    return {
      id: uuid.v4(),
      gameSlug: betInfo.game_slug,
      gameName: betInfo.game_name,
      pick: item.pick,
      odds,
      outComeId: item.market_options_id,
      outComeName: item.market_name,
      marketName: item.market_name,
      marketOptionName: item.market_options_name,
      drawDate: item.draw_date,
      eventId: item.event_id,
      numbers: filterAndSortNumbers(balls, item.numbers[0]),
      isCombination,
      wager,
      returnValue: calculateBetReturnValue(wager, odds, 1),
      betSettings: settings,
    };
  });
}
